import { useEffect, useState } from "react";
import { BsX, BsGeoAlt, BsPlusLg, BsExclamationTriangle } from "react-icons/bs";
import axiosAdmin from "../../../../api/apiAdmin";
import { apiError } from "../../../../api/apiError";
import { errorToast, successToast } from "../../toastAlerts/ToastAlerts";
import georgianCities from "../../../../data/georgianCities";

/**
 * Shown when an admin gives a parcel to a different customer: pick where it
 * goes, or add an address for them on the spot.
 *
 * The address belongs to the recipient. The server refuses one that isn't
 * theirs, and a recipient change with no address chosen clears it rather
 * than keeping the previous owner's — which is how parcels ended up with a
 * stranger's address pre-filled in the courier form.
 *
 * onConfirm(addressId | null): null means "assign without an address"; the
 * parcel then can't go to OnWay until the customer adds one.
 */
const cityLabel = (v) => georgianCities.find((c) => c.value === v)?.label || v;

const field =
  "w-full h-10 px-3 rounded-lg border border-slate-300 dark:border-slate-600 dark:bg-slate-800 dark:text-white text-sm outline-none focus:border-teal_600";

const RecipientAddressModal = ({ packId, tracking, userId, onConfirm, onCancel }) => {
  const [loading, setLoading] = useState(true);
  const [user, setUser] = useState(null);
  const [addresses, setAddresses] = useState([]);
  const [selected, setSelected] = useState(null);
  const [adding, setAdding] = useState(false);
  const [form, setForm] = useState({ city: "Tbilisi", address: "", phone: "", is_default: false });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    setLoading(true);
    axiosAdmin
      .get(`/packs/${packId}/recipient-addresses`, { params: { user_id: userId } })
      .then(({ data }) => {
        setUser(data.user);
        setAddresses(data.addresses || []);
        const pick =
          data.current_address_id ||
          data.addresses?.find((a) => a.is_default)?.id ||
          data.addresses?.[0]?.id ||
          null;
        setSelected(pick);
        setAdding(!(data.addresses || []).length);
        setForm((f) => ({ ...f, phone: data.user?.phone || "", is_default: !(data.addresses || []).length }));
      })
      .catch((err) => {
        errorToast(apiError(err, "მომხმარებელი ვერ მოიძებნა"));
        onCancel();
      })
      .finally(() => setLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [packId, userId]);

  const addAddress = async () => {
    if (!form.address.trim()) {
      errorToast("ჩაწერეთ მისამართი");
      return;
    }
    setSaving(true);
    try {
      const { data } = await axiosAdmin.post(`/packs/${packId}/recipient-addresses`, {
        user_id: userId,
        ...form,
        address: form.address.trim(),
      });
      const a = data.address;
      setAddresses((list) => [
        ...(a.is_default ? list.map((x) => ({ ...x, is_default: false })) : list),
        { ...a, is_default: !!a.is_default },
      ]);
      setSelected(a.id);
      setAdding(false);
      setForm((f) => ({ ...f, address: "" }));
      successToast("მისამართი დაემატა მომხმარებელს");
    } catch (err) {
      errorToast(apiError(err, "მისამართი ვერ დაემატა"));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4"
      onClick={onCancel}
    >
      <div
        className="w-full max-w-[560px] max-h-[88vh] overflow-auto bg-white dark:bg-slate-700 rounded-2xl shadow-xl p-6 sm:p-4 flex flex-col gap-4"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-start justify-between gap-3">
          <div>
            <p className="font-bold text-lg text-slate-800 dark:text-white">მიწოდების მისამართი</p>
            <p className="text-xs text-slate-500 dark:text-slate-300">
              ამანათი {tracking} გადადის მომხმარებელზე
              {user ? ` #${user.id} ${user.name}${user.phone ? ` · ${user.phone}` : ""}` : ""}
            </p>
          </div>
          <BsX className="text-2xl text-slate-400 cursor-pointer shrink-0" onClick={onCancel} />
        </div>

        {loading ? (
          <p className="py-8 text-center text-slate-400">…</p>
        ) : (
          <>
            {addresses.length > 0 && (
              <div className="flex flex-col gap-2">
                {addresses.map((a) => (
                  <label
                    key={a.id}
                    className={`flex items-start gap-3 rounded-xl border p-3 cursor-pointer ${
                      selected === a.id
                        ? "border-teal_600 bg-teal-50 dark:bg-teal-900/30"
                        : "border-slate-200 dark:border-slate-600"
                    }`}
                  >
                    <input
                      type="radio"
                      name="recipient-address"
                      className="mt-1"
                      checked={selected === a.id}
                      onChange={() => setSelected(a.id)}
                    />
                    <div className="min-w-0 text-sm dark:text-slate-100">
                      <p className="font-bold flex items-center gap-2 flex-wrap">
                        <BsGeoAlt className="text-teal_600" /> {cityLabel(a.city)}
                        {a.is_default && (
                          <span className="text-[10px] font-bold rounded-full bg-teal_600 text-white px-2 py-0.5">ძირითადი</span>
                        )}
                        {a.is_highland && (
                          <span className="text-[10px] font-bold rounded-full bg-amber-100 text-amber-800 px-2 py-0.5">მაღალმთიანი</span>
                        )}
                      </p>
                      <p className="break-words">{a.address}</p>
                      {a.phone && <p className="text-xs text-slate-500 dark:text-slate-300">{a.phone}</p>}
                    </div>
                  </label>
                ))}
              </div>
            )}

            {!adding ? (
              <button
                type="button"
                onClick={() => setAdding(true)}
                className="self-start flex items-center gap-2 text-sm font-bold text-teal_600"
              >
                <BsPlusLg /> ახალი მისამართის დამატება
              </button>
            ) : (
              <div className="rounded-xl border border-dashed border-slate-300 dark:border-slate-600 p-3 flex flex-col gap-2">
                <p className="text-xs font-bold text-slate-500 dark:text-slate-300">
                  {addresses.length ? "ახალი მისამართი" : "მომხმარებელს მისამართი არ აქვს — დაამატეთ"}
                </p>
                <select
                  className={field}
                  value={form.city}
                  onChange={(e) => setForm((f) => ({ ...f, city: e.target.value }))}
                >
                  {georgianCities.map((c) => (
                    <option key={c.value} value={c.value}>
                      {c.label}
                    </option>
                  ))}
                </select>
                <input
                  className={field}
                  placeholder="ქუჩა, ნომერი, ბინა"
                  value={form.address}
                  onChange={(e) => setForm((f) => ({ ...f, address: e.target.value }))}
                />
                <input
                  className={field}
                  placeholder="ტელეფონი (არასავალდებულო)"
                  value={form.phone}
                  onChange={(e) => setForm((f) => ({ ...f, phone: e.target.value }))}
                />
                {addresses.length > 0 && (
                  <label className="flex items-center gap-2 text-xs dark:text-slate-200">
                    <input
                      type="checkbox"
                      checked={form.is_default}
                      onChange={(e) => setForm((f) => ({ ...f, is_default: e.target.checked }))}
                    />
                    ძირითად მისამართად დაყენება
                  </label>
                )}
                <div className="flex gap-2">
                  <button
                    type="button"
                    onClick={addAddress}
                    disabled={saving}
                    className="rounded-lg bg-teal_600 disabled:opacity-50 text-white text-sm font-bold px-4 py-2"
                  >
                    {saving ? "…" : "დამატება"}
                  </button>
                  {addresses.length > 0 && (
                    <button
                      type="button"
                      onClick={() => setAdding(false)}
                      className="rounded-lg text-sm font-bold px-4 py-2 text-slate-500"
                    >
                      გაუქმება
                    </button>
                  )}
                </div>
              </div>
            )}

            {!selected && (
              <p className="flex items-start gap-2 text-xs text-amber-800 bg-amber-50 dark:bg-amber-900/30 dark:text-amber-200 rounded-lg px-3 py-2">
                <BsExclamationTriangle className="mt-0.5 shrink-0" />
                მისამართის გარეშე ამანათი მიენიჭება, მაგრამ OnWay-ს ვერ გაეგზავნება, სანამ მისამართი არ დაემატება.
              </p>
            )}

            <div className="flex flex-wrap gap-2 justify-end pt-1">
              <button
                type="button"
                onClick={onCancel}
                className="rounded-lg text-sm font-bold px-4 py-2.5 text-slate-500"
              >
                გაუქმება
              </button>
              {!selected && (
                <button
                  type="button"
                  onClick={() => onConfirm(null)}
                  className="rounded-lg bg-slate-500 text-white text-sm font-bold px-4 py-2.5"
                >
                  მისამართის გარეშე მინიჭება
                </button>
              )}
              {selected && (
                <button
                  type="button"
                  onClick={() => onConfirm(selected)}
                  className="rounded-lg bg-teal_600 text-white text-sm font-bold px-5 py-2.5"
                >
                  მინიჭება ამ მისამართით
                </button>
              )}
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default RecipientAddressModal;
